import { useState, useEffect } from 'react';
import { mediaUrl } from '../utils/media';
import Navbar from '../components/common/Navbar';

import ChatBubble from '../components/common/ChatBubble';
import EbookCard from '../components/catalog/EbookCard';
import StarRating from '../components/catalog/StarRating';
import PdfPreview from '../components/catalog/PdfPreview';
import PaymentModal from '../components/checkout/PaymentModal';
import api from '../utils/api';
import { getUser } from '../utils/auth';
import { useNavigate } from 'react-router-dom';
import { ShoppingCartIcon, BookOpenIcon, Bars3Icon, ShareIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const CATEGORIES = ['Semua', 'Bisnis', 'Teknologi', 'Keuangan', 'Produktivitas', 'Kesehatan', 'Gaya Hidup'];

const highlight = (text, q) => {
  if (!q) return text;
  const idx = text.toLowerCase().indexOf(q.toLowerCase());
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark className="bg-yellow-200 rounded px-0.5">{text.slice(idx, idx + q.length)}</mark>
      {text.slice(idx + q.length)}
    </>
  );
};

export default function HomePage() {
  const [ebooks, setEbooks] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState(false);
  const [activeOrder, setActiveOrder] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(typeof window !== 'undefined' && window.innerWidth >= 768);
  const [cart, setCart] = useState([]);
  const [showCart, setShowCart] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Semua');
  const [sortBy, setSortBy] = useState('terbaru');
  const [reviews, setReviews] = useState([]);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [recs, setRecs] = useState([]);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const [ownedIds, setOwnedIds] = useState(new Set());
  const [cartMode, setCartMode] = useState(false); // true = PaymentModal for cart checkout
  const navigate = useNavigate();
  const user = getUser();

  useEffect(() => {
    api.get(`/api/ebooks?sort=${sortBy}`)
      .then((r) => {
        setEbooks(r.data); setFiltered(r.data);
        // Share link: /?share=<id> → auto-open ebook detail
        const shareId = new URLSearchParams(window.location.search).get('share');
        if (shareId) {
          const found = r.data.find(e => e.id === shareId);
          if (found) {
            setSelected(found);
            setSidebarOpen(false);
            window.history.replaceState({}, '', window.location.pathname);
          }
        }
      })
      .catch(() => toast.error('Gagal memuat ebook'))
      .finally(() => setLoading(false));
  }, [sortBy]);

  const refreshOwned = () => {
    if (!user) return;
    api.get('/api/library')
      .then(r => setOwnedIds(new Set(r.data.map(e => e.ebook_id || e.id))))
      .catch(() => {});
  };

  useEffect(() => { refreshOwned(); }, [user]);

  useEffect(() => {
    const q = search.toLowerCase();
    let result = ebooks.filter(
      (e) => e.title.toLowerCase().includes(q) || (e.seller_name || '').toLowerCase().includes(q)
    );
    if (activeCategory !== 'Semua') {
      result = result.filter(e => e.tags?.some(t => t.toLowerCase().includes(activeCategory.toLowerCase())));
    }
    setFiltered(result);
  }, [search, ebooks, activeCategory]);

  useEffect(() => {
    if (!selected?.id) { setReviews([]); return; }
    api.get(`/api/reviews/${selected.id}`)
      .then(r => setReviews(r.data))
      .catch(() => setReviews([]));
  }, [selected]);

  useEffect(() => {
    setRecs(
      ebooks
        .filter(e => e.id !== selected?.id && e.tags?.some(t => selected?.tags?.includes(t)))
        .slice(0, 3)
    );
  }, [selected, ebooks]);

  const handleBuy = () => {
    if (!user) { navigate('/login'); return; }
    setShowOrderSummary(true);
  };

  const confirmBuy = async (voucherCode) => {
    setShowOrderSummary(false);
    setBuying(true);
    try {
      const res = await api.post('/api/orders', { ebook_id: selected.id, voucher_code: voucherCode });
      setActiveOrder(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal membuat order');
    } finally {
      setBuying(false);
    }
  };

  // Cart checkout — konfirmasi lalu buat order real
  const confirmCartBuy = async (voucherCode) => {
    setCartMode(false);
    setBuying(true);
    try {
      const res = await api.post('/api/orders/cart', { ebook_ids: cart.map(c => c.id), voucher_code: voucherCode });
      setCart([]);
      setShowCart(false);
      setActiveOrder({
        id: res.data.orders[0]?.id,
        amount: res.data.total_amount,
        payment_status: 'pending',
        ebook_title: `${cart.length} ebook`,
      });
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(Array.isArray(d) ? d.map(x=>x.msg).join(', ') : (d||'Gagal checkout'));
    } finally {
      setBuying(false);
    }
  };

  const addToCart = (ebook) => {
    if (cart.find(c => c.id === ebook.id)) {
      toast('Sudah di keranjang');
      return;
    }
    setCart(p => [...p, ebook]);
    toast.success('Ditambah ke keranjang');
  };

  const removeFromCart = (id) => setCart(p => p.filter(c => c.id !== id));

  const submitReview = async () => {
    if (!reviewRating) { toast.error('Pilih rating dulu'); return; }
    setSubmittingReview(true);
    try {
      await api.post('/api/reviews', { ebook_id: selected.id, rating: reviewRating, comment: reviewComment });
      toast.success('Review berhasil dikirim');
      setReviewRating(0);
      setReviewComment('');
      const r = await api.get(`/api/reviews/${selected.id}`);
      setReviews(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengirim review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const hasReviewed = user && reviews.some(r => r.buyer_id === user.id || r.buyer_email === user.email);

  const now = new Date().toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' });

  return (
    <div className="flex flex-col h-screen bg-wa-bg pb-16 md:pb-0">
      <Navbar />
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'w-80' : 'w-0'} transition-all duration-300 overflow-hidden flex-shrink-0 bg-white border-r border-gray-200 flex flex-col`}>
          <div className="p-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-200">
              <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Cari ebook..."
                className="flex-1 text-sm outline-none bg-transparent"
              />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Memuat...</div>
            ) : filtered.length === 0 ? (
              <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Tidak ada ebook</div>
            ) : (
              filtered.map((ebook) => (
                <div
                  key={ebook.id}
                  onClick={() => { setSelected(ebook); if (window.innerWidth < 768) setSidebarOpen(false); }}
                  className={`p-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 transition min-w-0 overflow-hidden ${selected?.id === ebook.id ? 'bg-wa-light border-l-4 border-l-wa-dark' : ''}`}
                >
                  <EbookCard ebook={ebook} />
                </div>
              ))
            )}
          </div>
        </div>

        {/* Main content */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Top bar */}
          <div className="bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
            <button onClick={() => setSidebarOpen(p => !p)} className="text-gray-500 hover:text-gray-700">
              <Bars3Icon className="w-5 h-5" />
            </button>
            <button
              onClick={() => setShowCart(p => !p)}
              className="relative text-gray-500 hover:text-gray-700"
            >
              <ShoppingCartIcon className="w-5 h-5" />
              {cart.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 bg-wa-dark text-white text-xs w-4 h-4 rounded-full flex items-center justify-center">
                  {cart.length}
                </span>
              )}
            </button>
          </div>

          {/* Cart panel */}
          {showCart && cart.length > 0 && (
            <div className="bg-white border-b border-gray-200 p-4 shadow-sm">
              <p className="font-semibold text-gray-700 mb-2 text-sm">Keranjang ({cart.length})</p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {cart.map(item => (
                  <div key={item.id} className="flex items-center justify-between text-sm">
                    <span className="truncate flex-1 mr-2">{item.title}</span>
                    <span className="text-wa-dark font-medium whitespace-nowrap">
                      {item.price === 0 ? 'Gratis' : `Rp ${Number(item.price).toLocaleString('id-ID')}`}
                    </span>
                    <button onClick={() => removeFromCart(item.id)} className="ml-2 text-gray-400 hover:text-red-500 text-xs">✕</button>
                  </div>
                ))}
              </div>
              <div className="mt-3 pt-2 border-t border-gray-100 flex justify-between text-sm font-semibold">
                <span>Total</span>
                <span className="text-wa-dark">Rp {cart.reduce((s,c) => s + Number(c.final_price||c.price||0), 0).toLocaleString('id-ID')}</span>
              </div>
              <button
                onClick={() => {
                  if (!user) { navigate('/login'); return; }
                  // Fase 1: tampilkan konfirmasi pembelian (voucher + total), order belum dibuat
                  setCartMode(true);
                  setActiveOrder({
                    id: null,
                    amount: cart.reduce((s, c) => s + Number(c.final_price || c.price || 0), 0),
                    ebook_title: `${cart.length} ebook`,
                  });
                }}
                className="w-full bg-wa-dark text-white py-2.5 rounded-full font-medium hover:bg-green-900 transition mt-2"
              >
                Bayar Sekarang
              </button>
            </div>
          )}

          {selected ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* WA chat header — contact name bar */}
              <div className="bg-wa-dark px-4 py-2 flex items-center gap-3">
                <button onClick={() => setSelected(null)} className="text-green-200 hover:text-white text-xl leading-none">←</button>
                {selected.cover_url
                  ? <img src={mediaUrl(selected.cover_url)} alt={selected.title} className="w-8 h-8 rounded-full object-cover" />
                  : <div className="w-8 h-8 rounded-full bg-wa-light flex items-center justify-center"><BookOpenIcon className="w-4 h-4 text-wa-dark" /></div>
                }
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-semibold truncate">{selected.title}</p>
                  <p className="text-green-200 text-xs">{selected.seller_name || 'Penjual'}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      const slug = selected.slug || selected.title.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_]+/g, '-').replace(/-+/g, '-').slice(0, 60) || 'ebook';
                      const url = `${window.location.origin}/api/ebook/${slug}-${selected.id}`;
                      if (navigator.share) {
                        navigator.share({ title: selected.title, url }).catch(() => {});
                      } else {
                        navigator.clipboard.writeText(url).then(() => toast.success('Link disalin'));
                      }
                    }}
                    className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white p-1.5 rounded-full transition"
                    title="Bagikan"
                  >
                    <ShareIcon className="w-4 h-4" />
                  </button>
                  <button onClick={() => addToCart(selected)} className="bg-white bg-opacity-20 hover:bg-opacity-30 text-white text-xs px-3 py-1 rounded-full transition">+ Keranjang</button>
                  <button onClick={handleBuy} disabled={buying} className="bg-wa-green hover:bg-green-400 text-white text-xs px-3 py-1 rounded-full transition disabled:opacity-50">{buying ? '...' : 'Beli'}</button>
                </div>
              </div>

              {/* Chat messages area */}
              <div className="flex-1 overflow-y-auto p-4" style={{background: '#ECE5DD'}}>
                <ChatBubble side="left" time={now}>
                  {/* Cover */}
                  {selected.cover_url && (
                    <img src={mediaUrl(selected.cover_url)} alt={selected.title} className="w-full max-w-xs rounded-lg object-cover mb-3" />
                  )}

                  {/* Badges */}
                  <div className="flex gap-1 flex-wrap mb-1">
                    {selected.is_bestseller && <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full">🏆 Bestseller</span>}
                    {selected.is_new && !selected.is_bestseller && <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">✨ Baru</span>}
                    {selected.discount_pct > 0 && <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-{selected.discount_pct}%</span>}
                  </div>

                  {/* Title */}
                  <p className="font-semibold text-gray-800 text-base mb-0.5">{selected.title}</p>
                  <p className="text-gray-500 text-xs mb-1">{selected.seller_name || 'Penjual'}</p>
                  <StarRating rating={selected.avg_rating} count={selected.review_count} />

                  {/* Price */}
                  <div className="mt-1 mb-3">
                    {selected.discount_pct > 0 ? (
                      <div className="flex items-center gap-2">
                        <span className="text-wa-dark font-bold text-lg">Rp {Number(selected.final_price || selected.price).toLocaleString('id-ID')}</span>
                        <span className="text-gray-400 line-through text-sm">Rp {Number(selected.price).toLocaleString('id-ID')}</span>
                      </div>
                    ) : (
                      <span className="text-wa-dark font-bold text-lg">Rp {Number(selected.price).toLocaleString('id-ID')}</span>
                    )}
                  </div>

                  {/* Description */}
                  {selected.description && (
                    <p className="text-gray-600 text-sm leading-relaxed mb-3 border-t border-gray-100 pt-2">{selected.description}</p>
                  )}

                  {/* PDF preview */}
                  {selected.has_preview && (
                    <div className="mb-3">
                      <PdfPreview url={`/api/ebooks/${selected.id}/preview`} />
                    </div>
                  )}

                  {/* Reviews */}
                  <div className="border-t border-gray-100 pt-2 mt-1">
                    <p className="text-xs font-semibold text-gray-500 mb-2">Ulasan ({reviews.length})</p>
                    {reviews.length > 0 ? (
                      <div className="space-y-2">
                        {reviews.map((rv, i) => (
                          <div key={i} className="border-b border-gray-100 pb-2 last:border-0">
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-medium text-gray-700">{rv.buyer_name || 'Pembeli'}</span>
                              <StarRating rating={rv.rating} />
                            </div>
                            {rv.comment && <p className="text-xs text-gray-600 mt-0.5">{rv.comment}</p>}
                          </div>
                        ))}
                      </div>
                    ) : <p className="text-xs text-gray-400">Belum ada ulasan.</p>}
                  </div>

                  {/* Write review */}
                  {user && !hasReviewed && ownedIds.has(selected.id) && (
                    <div className="border-t border-gray-100 pt-2 mt-2">
                      <p className="text-xs font-semibold mb-1">Tulis Ulasan</p>
                      <StarRating rating={reviewRating} size="lg" onRate={setReviewRating} />
                      <textarea value={reviewComment} onChange={e => setReviewComment(e.target.value)}
                        placeholder="Komentar..." rows={2}
                        className="w-full mt-1 border border-gray-200 rounded p-1.5 text-xs outline-none focus:border-wa-dark resize-none"
                      />
                      <button onClick={submitReview} disabled={submittingReview}
                        className="mt-1 w-full bg-wa-dark text-white py-1.5 rounded-full text-xs font-medium hover:bg-green-900 transition disabled:opacity-50">
                        {submittingReview ? 'Mengirim...' : 'Kirim Ulasan'}
                      </button>
                    </div>
                  )}

                  {/* Recs */}
                  {recs.length > 0 && (
                    <div className="border-t border-gray-100 pt-2 mt-2">
                      <p className="text-xs font-semibold text-gray-500 mb-2">Orang lain juga beli:</p>
                      <div className="space-y-2">
                        {recs.map(r => (
                          <div key={r.id} onClick={() => setSelected(r)} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded p-1">
                            <img src={mediaUrl(r.cover_url)} alt={r.title} className="w-10 h-10 rounded object-cover" />
                            <div>
                              <p className="text-xs font-medium text-gray-800 truncate">{r.title}</p>
                              <p className="text-xs text-wa-dark font-bold">Rp {Number(r.final_price || r.price).toLocaleString('id-ID')}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </ChatBubble>
              </div>
            </div>
          ) : (
            /* Catalog grid */
            <div className="flex-1 overflow-y-auto">
              {/* Category filter pills + sort */}
              <div className="flex items-center gap-2 overflow-x-auto p-4 pb-0">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setActiveCategory(cat)}
                    className={`px-4 py-1.5 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition ${
                      activeCategory === cat
                        ? 'bg-wa-dark text-white'
                        : 'bg-white text-gray-600 border border-gray-200 hover:border-wa-dark'
                    }`}
                  >
                    {cat}
                  </button>
                ))}
                <select
                  value={sortBy}
                  onChange={e => setSortBy(e.target.value)}
                  className="ml-auto text-sm border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 bg-white"
                >
                  <option value="terbaru">Terbaru</option>
                  <option value="terpopuler">Terpopuler</option>
                  <option value="harga_asc">Harga ↑</option>
                  <option value="harga_desc">Harga ↓</option>
                </select>
              </div>

              {loading ? (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Memuat...</div>
              ) : filtered.length === 0 ? (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Tidak ada ebook</div>
              ) : (
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 p-4">
                  {filtered.map(ebook => (
                    <div
                      key={ebook.id}
                      onClick={() => setSelected(ebook)}
                      className="bg-white rounded-xl shadow-sm overflow-hidden cursor-pointer hover:shadow-md transition"
                    >
                      {ebook.cover_url ? (
                        <img src={mediaUrl(ebook.cover_url)} alt={ebook.title} className="w-full h-32 object-cover" />
                      ) : (
                        <div className="w-full h-32 bg-wa-light flex items-center justify-center">
                          <BookOpenIcon className="w-10 h-10 text-wa-dark" />
                        </div>
                      )}
                      <div className="p-2">
                        {/* Badge row */}
                        <div className="flex gap-1 flex-wrap mb-1">
                          {ebook.is_bestseller && (
                            <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-2 py-0.5 rounded-full">🏆 Bestseller</span>
                          )}
                          {ebook.is_new && !ebook.is_bestseller && (
                            <span className="bg-green-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">✨ Baru</span>
                          )}
                          {ebook.discount_pct > 0 && (
                            <span className="bg-red-500 text-white text-xs font-bold px-2 py-0.5 rounded-full">-{ebook.discount_pct}%</span>
                          )}
                        </div>
                        <p className="text-gray-800 text-xs font-medium truncate">{highlight(ebook.title, search)}</p>
                        <p className="text-gray-500 text-xs truncate">{ebook.seller_name || 'Penjual'}</p>
                        {ebook.discount_pct > 0 ? (
                          <div className="flex items-center gap-1 mt-0.5">
                            <span className="text-wa-dark font-bold text-sm">
                              Rp {Number(ebook.final_price || ebook.price).toLocaleString('id-ID')}
                            </span>
                            <span className="text-gray-400 line-through text-xs">
                              Rp {Number(ebook.price).toLocaleString('id-ID')}
                            </span>
                          </div>
                        ) : (
                          <p className="text-wa-dark text-xs font-semibold mt-0.5">
                            {ebook.price === 0 ? 'Gratis' : `Rp ${Number(ebook.price).toLocaleString('id-ID')}`}
                          </p>
                        )}
                        <StarRating rating={ebook.avg_rating} count={ebook.review_count} />
                        <button
                          onClick={e => { e.stopPropagation(); addToCart(ebook); }}
                          className="mt-1.5 w-full text-xs border border-wa-dark text-wa-dark py-1 rounded-full hover:bg-wa-light transition"
                        >
                          + Keranjang
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {(showOrderSummary || activeOrder) && (selected || cartMode) && (
        <PaymentModal
          order={activeOrder || { id: null, ebook_title: cartMode ? `${cart.length} ebook` : selected.title, amount: cartMode ? cart.reduce((s, c) => s + Number(c.final_price || c.price || 0), 0) : (selected.final_price || selected.price), status: 'pending' }}
          ebook={cartMode ? (cart[0] || {}) : selected}
          user={user}
          buying={buying}
          onConfirm={cartMode ? confirmCartBuy : confirmBuy}
          onClose={() => { setShowOrderSummary(false); setActiveOrder(null); setCartMode(false); }}
          onSuccess={() => { setShowOrderSummary(false); setActiveOrder(null); setCartMode(false); refreshOwned(); navigate('/library'); }}
        />
      )}
    </div>
  );
}
